import autobind from "autobind-decorator";
import { action, observable } from "mobx";
import { cloneDeepSafe } from "@lib/common";
import { Store } from "@lib/components/src/components/table/pro/store";
import { BookingDatePreset, UPCOMING_SORT_KEY } from "./bookings-date-range-filter";

export { UPCOMING_SORT_KEY };
export const BOOKING_SORT_KEY = "config.timestamp";
export const PLACED_SORT_KEY = "created";
const BOOKING_SORT_DIRECTION = -1 as const;
const PLACED_SORT_DIRECTION = -1 as const;

export type BookingsTableFiltersState = {
	search: string;
	sortKey: string;
	sortDirection: 1 | -1;
	filtersActive: {
		[key: string]: {
			$in: string[];
		};
	};
	fromDate?: number;
	toDate?: number;
	datePreset: BookingDatePreset;
	page: number;
};

export function defaultBookingsTableFilters(): BookingsTableFiltersState {
	return {
		search: "",
		sortKey: BOOKING_SORT_KEY,
		sortDirection: BOOKING_SORT_DIRECTION,
		filtersActive: {},
		fromDate: undefined,
		toDate: undefined,
		datePreset: "all",
		page: 1,
	};
}

type StoreOptions = {
	initial?: BookingsTableFiltersState;
	onPersist?: (state: BookingsTableFiltersState) => void;
};

/** Bookings-only Protable store: default sort by booking date, not created. */
@autobind
export class BookingsTableStore<T> extends Store<T> {
	private timezone: string;
	private onPersist?: (state: BookingsTableFiltersState) => void;

	@observable datePreset: BookingDatePreset = "all";

	constructor(
		fetch: T.Lib.Table.FetchFunction,
		timezone: string,
		options?: StoreOptions
	) {
		super(fetch);
		this.timezone = timezone;
		this.onPersist = options?.onPersist;
		this.restoreFiltersState(options?.initial ?? defaultBookingsTableFilters());
	}

	getFiltersState = (): BookingsTableFiltersState => ({
		search: this.s.search,
		sortKey: this.s.sortKey,
		sortDirection: this.s.sortDirection,
		filtersActive: cloneDeepSafe(this.s.filtersActive),
		fromDate: this.s.fromDate,
		toDate: this.s.toDate,
		datePreset: this.datePreset,
		page: this.s.page,
	});

	@action
	restoreFiltersState = (state: BookingsTableFiltersState) => {
		this.datePreset = state.datePreset;
		this.update(
			{
				search: state.search,
				sortKey: state.sortKey,
				sortDirection: state.sortDirection,
				filtersActive: cloneDeepSafe(state.filtersActive),
				page: state.page,
			},
			{ skipPersist: true }
		);
		this.setDateRange(state.fromDate, state.toDate, { skipPersist: true });
	};

	private persistFilters = () => {
		this.onPersist?.(this.getFiltersState());
	};

	@action
	update = (
		data: Partial<Store<T>["s"]>,
		options?: { skipPersist?: boolean }
	) => {
		for (const key in data) {
			if (data.hasOwnProperty(key)) {
				const value = data[key as keyof Store<T>["s"]];
				if (value !== undefined) {
					//@ts-ignore
					this.s[key as keyof Store<T>["s"]] = value;
				}
			}
		}
		if (!options?.skipPersist) {
			this.persistFilters();
		}
	};

	@action
	setDateRange = (
		start?: number,
		end?: number,
		options?: { skipPersist?: boolean }
	) => {
		this.s.fromDate = start;
		this.s.toDate = end;
		if (!options?.skipPersist) {
			this.persistFilters();
		}
	};

	@action
	setDatePreset = (preset: BookingDatePreset) => {
		this.datePreset = preset;
		this.persistFilters();
	};

	applyDefaultDateRange = () => {
		this.setDateRange(undefined, undefined);
	};

	/** Today/Tomorrow: newest placed first; All Time: booking date default. */
	@action
	applySortForDatePreset = (preset: BookingDatePreset) => {
		if (preset === "today" || preset === "tomorrow") {
			this.update({
				sortKey: PLACED_SORT_KEY,
				sortDirection: PLACED_SORT_DIRECTION,
			});
		} else if (preset === "all") {
			this.update({
				sortKey: BOOKING_SORT_KEY,
				sortDirection: BOOKING_SORT_DIRECTION,
			});
		}
	};

	@action
	reset = () => {
		this.datePreset = "all";
		this.applyDefaultDateRange();
		this.update({
			search: "",
			sortKey: BOOKING_SORT_KEY,
			sortDirection: BOOKING_SORT_DIRECTION,
			filtersActive: {},
			page: 1,
		});
	};

	@action
	filterToggle = (key: string, value: string) => {
		if (this.s.filtersActive[key]) {
			const index = this.s.filtersActive[key].$in.indexOf(value);
			if (index === -1) this.s.filtersActive[key].$in.push(value);
			else this.s.filtersActive[key].$in.splice(index, 1);
		} else {
			this.s.filtersActive[key] = { $in: [value] };
		}
		this.persistFilters();
	};

	@action
	setPage = (page: number) => {
		this.s.page = page;
		this.persistFilters();
		this.fetch();
	};
}
